
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

/**
 * StickyBottomBanner Component
 *
 * Fixed-position banner displaying promotional offers and countdown timer.
 * Adapts messaging and behavior based on billing interval (monthly/yearly).
 *
 * @component
 * @example
 * ```vue
 * <StickyBottomBanner
 *   :is-yearly="false"
 *   :countdown-text="'25:30'"
 *   :is-expired="false"
 *   :store-id="123"
 *   :countdown-color="'green'"
 *   :is-submitting="false"
 *   @cta-click="handleCtaClick"
 *   @expired="handleExpired"
 * />
 * ```
 *
 * @remarks
 * Features:
 * - Sticky positioning at bottom of viewport
 * - Color-coded countdown with urgency animations
 * - Different messaging for monthly vs yearly billing
 * - Auto-hides when timer expires (monthly only)
 * - Cannot be dismissed by users (no close button)
 * - Emits events for CTA click and expiration
 *
 * Monthly Behavior:
 * - Shows countdown timer with urgency colors
 * - Hides automatically when timer expires
 * - CTA: "Claim Discount"
 * - Message: Up to 60% OFF first month
 *
 * Yearly Behavior:
 * - No countdown timer (always-available offer)
 * - Always visible (never expires)
 * - CTA: "See Plans"
 * - Message: 2 months free with yearly
 *
 * @emits cta-click - User clicked CTA button
 * @emits expired - Timer expired (monthly only)
 */
@Component({
  components: {},
})
export default class StickyBottomBanner extends Vue {
  /** Whether yearly billing is selected */
  @Prop({ required: true })
  public isYearly!: boolean;

  /** Countdown timer text (e.g., "25:30" or "5 min") */
  @Prop({ default: '' })
  public countdownText!: string;

  /** Whether countdown timer has expired */
  @Prop({ required: true })
  public isExpired!: boolean;

  /** Shopify store ID for tracking */
  @Prop({ required: true })
  public storeId!: number;

  /** Color code for countdown urgency ('green' | 'orange' | 'red') */
  @Prop({ default: 'green' })
  public countdownColor!: 'green' | 'orange' | 'red';

  /** Whether user is currently submitting a package subscription */
  @Prop({ default: false })
  public isSubmitting!: boolean;

  /** Offer message to display */
  @Prop({ default: 'Limited Time: Get up to 60% OFF your first month of Replenishment Center!' })
  public offerMessage!: string;

  /**
   * Get banner icon emoji based on billing interval
   *
   * @returns {string} Emoji icon
   *
   * @remarks
   * Icons convey offer type:
   * - Monthly (🎉): Celebration - time-limited opportunity
   * - Yearly (🎁): Gift - special bonus included
   */
  get bannerIcon(): string {
    return this.isYearly ? '🎁' : '🎉';
  }

  /**
   * Get banner message based on billing interval
   *
   * @returns {string} Promotional message text
   *
   * @remarks
   * - Monthly: Emphasizes urgency and discount percentage
   * - Yearly: Highlights value and business benefit
   */
  get bannerText(): string {
    if (this.isYearly) {
      return 'Yearly plans include 2 months FREE - Best value for growing businesses!';
    } else {
      return this.offerMessage;
    }
  }

  /**
   * Get CTA button text based on billing interval
   *
   * @returns {string} Call-to-action button text
   *
   * @remarks
   * - Monthly: Action-oriented ("Claim Discount")
   * - Yearly: Exploratory ("See Plans")
   */
  get ctaText(): string {
    return this.isYearly ? 'Subscribe Now' : 'Claim Discount';
  }

  /**
   * Determine if banner should be visible
   *
   * @returns {boolean} True if banner should display
   *
   * @remarks
   * Visibility rules:
   * - Hide during package submission (isSubmitting = true)
   * - Monthly: Hide when timer expires
   * - Yearly: Always show (no expiration)
   * - Cannot be dismissed by user
   *
   * @example
   * ```typescript
   * // Banner hidden during subscription process
   * if (this.isSubmitting) return false;
   *
   * // Banner hidden after monthly timer expires
   * if (!this.isYearly && this.isExpired) return false;
   * ```
   */
  get shouldShowBanner(): boolean {
    // Hide banner during package submission to improve UX
    // Prevents banner from showing during redirect/confirmation flow
    if (this.isSubmitting) {
      return false;
    }

    // For monthly plans, hide if timer expired
    if (!this.isYearly && this.isExpired) {
      return false;
    }

    return true;
  }

  /**
   * Handle CTA button click
   *
   * Emits cta-click event to notify parent component.
   * Parent component is responsible for scrolling to pricing cards
   * and handling any subsequent actions.
   *
   * @fires cta-click - Emitted when user clicks the CTA button
   */
  handleCTAClick() {
    this.$emit('cta-click');
  }

  /**
   * Watch for timer expiration
   *
   * Auto-hides banner and emits event when countdown reaches zero.
   * Only applies to monthly billing (yearly has no timer).
   *
   * @param {boolean} newVal - New expired state
   * @fires expired - When timer expires
   *
   * @remarks
   * - Only triggers for monthly plans
   * - Allows parent to track expiration analytics
   * - Banner hides automatically via shouldShowBanner
   */
  @Watch('isExpired')
  onExpiredChange(newVal: boolean) {
    if (newVal && !this.isYearly) {
      // Timer expired for monthly plan
      this.$emit('expired');
    }
  }
}
